import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { getUserInfoFromStore, ISupportRequestUserInfo, redirectToSignIn, sendSupportRequestToMsi } from './utils/helpers';
import { showSuccessAlert, showErrorAlert } from './services/globalAlertService';
import {
	getRateLimitRemainingSeconds,
	hideOverlay,
	isRateLimited,
	removeRateLimit,
	setRateLimit,
	showOverlay,
	showRateLimitOverlay,
} from './utils/rateLimitHelper';
import ModalManager from './shared/components/modal/ModalManager';
import {
	trackExceptionWithContext,
	trackEventWithContext,
	LogLevel,
	type LogLevelType,
} from './services/application-insights/appInsights';
import {
	AppInsightsActions,
	AppInsightsComponents,
	AppInsightsErrorTypes,
} from './services/application-insights/appInsightsConstants';
import { shouldTrackStatusAsException } from './services/application-insights/appInsightsConfig';

interface IHttpMethodMessages {
	loading: string;
	success: string;
	error: string;
}

interface IApiMessages {
	GET: IHttpMethodMessages;
	POST: IHttpMethodMessages;
	PUT: IHttpMethodMessages;
	PATCH: IHttpMethodMessages;
	DELETE: IHttpMethodMessages;
	DEFAULT: IHttpMethodMessages;
}

const API_MESSAGES: IApiMessages = {
	GET: {
		loading: 'Retrieving data...',
		success: 'Data retrieved successfully',
		error: 'Failed to retrieve data',
	},
	POST: {
		loading: 'Creating new record...',
		success: 'Record created successfully',
		error: 'Failed to create record',
	},
	PUT: {
		loading: 'Updating record...',
		success: 'Record updated successfully',
		error: 'Failed to update record',
	},
	PATCH: {
		loading: 'Applying changes...',
		success: 'Changes applied successfully',
		error: 'Failed to apply changes',
	},
	DELETE: {
		loading: 'Removing record...',
		success: 'Record removed successfully',
		error: 'Failed to remove record',
	},
	DEFAULT: {
		loading: 'Processing request...',
		success: 'Operation completed successfully',
		error: 'Operation failed',
	},
} as const;

export const axiosWithToast = async <T = any>(
	axiosConfig: AxiosRequestConfig,
	message?: string,
	successMessage?: string,
	successAutoDismissTime: number = 5000,
	errorAutoDismissTime: number = 5000,
	showSuccessMessage: boolean = true,
): Promise<AxiosResponse<T>> => {
	const startTime = Date.now();
	const requestId = `${axiosConfig.method?.toUpperCase()}-${axiosConfig.url}-${startTime}`;

	// Check if currently rate limited
	if (isRateLimited()) {
		showRateLimitOverlay();

		// Track rate limit hit
		trackExceptionWithContext(new Error('Rate limited. Please wait before making more requests.'), {
			component: AppInsightsComponents.AXIOS_INSTANCE,
			action: AppInsightsActions.API_REQUEST_FAILURE,
			severity: LogLevel.Warning,
			additionalData: {
				requestId,
				errorType: AppInsightsErrorTypes.NETWORK_ERROR,
				url: axiosConfig.url || 'unknown',
				method: axiosConfig.method || 'unknown',
			},
		});

		throw new Error('Rate limited. Please wait before making more requests.');
	}

	const methodMessages = API_MESSAGES[axiosConfig.method?.toUpperCase() as keyof typeof API_MESSAGES] || API_MESSAGES.DEFAULT;
	const loadingMessage = message || methodMessages.loading;

	// Show overlay with loading spinner
	showOverlay(loadingMessage);

	try {
		const response = await authApiV2(axiosConfig);

		// Hide overlay and show success alert
		hideOverlay();
		if (showSuccessMessage) {
			const finalSuccessMessage = successMessage || handleSuccessMessage(response.data, methodMessages.success);
			showSuccessAlert(finalSuccessMessage, { autoDismissTime: successAutoDismissTime });
		}
		return response;
	} catch (error: any) {
		const duration = Date.now() - startTime;

		// Only track certain errors as exceptions to reduce noise in Application Insights
		const shouldTrackAsException = shouldTrackErrorAsException(error);

		if (shouldTrackAsException) {
			// Track API error with optimized context (reduced payload size)
			trackExceptionWithContext(error, {
				component: AppInsightsComponents.AXIOS_INSTANCE,
				action: AppInsightsActions.API_RESPONSE_ERROR,
				severity: getErrorSeverity(error),
				additionalData: {
					requestId,
					method: axiosConfig.method || 'unknown',
					url: axiosConfig.url || 'unknown',
					statusCode: error.response?.status?.toString() || 'no-response',
					duration: duration.toString(),
					errorType: categorizeError(error),
					// Removed: baseURL, responseData (potentially large/sensitive)
				},
			});
		} else {
			// For non-exception errors (like 401, 403), track as events instead
			trackEventWithContext('API_Error_Event', {
				component: AppInsightsComponents.AXIOS_INSTANCE,
				action: AppInsightsActions.API_RESPONSE_ERROR,
				severity: getErrorSeverity(error),
				additionalData: {
					requestId,
					method: axiosConfig.method || 'unknown',
					url: axiosConfig.url || 'unknown',
					statusCode: error.response?.status?.toString() || 'no-response',
					duration: duration.toString(),
					errorType: categorizeError(error),
				},
			});
		}

		// Hide overlay and show error alert
		hideOverlay();
		const errorMessage = getUserFriendlyErrorMessage(error, methodMessages.error);
		showErrorAlert(errorMessage, { autoDismissTime: errorAutoDismissTime });

		throw error;
	}
};

const getUserFriendlyErrorMessage = (error: any, fallbackMessage: string): string => {
	// Network errors (no response from server)
	if (!error.response) {
		if (error.code === 'NETWORK_ERROR' || error.message?.includes('Network Error')) {
			return 'Unable to connect to the server. Please check your internet connection.';
		}
		if (error.code === 'ENOTFOUND' || error.message?.includes('ENOTFOUND')) {
			return 'Server is currently unavailable. Please try again later.';
		}
		if (error.code === 'TIMEOUT' || error.message?.includes('timeout')) {
			return 'Request timed out. Please try again.';
		}
		return 'Network connection error. Please check your connection and try again.';
	}

	const status = error.response.status;
	const responseData = error.response.data;

	// Log technical details for debugging (only in development)
	if (process.env.NODE_ENV === 'development') {
		console.error('API Error Details:', {
			status,
			url: error.config?.url,
			method: error.config?.method,
			data: responseData,
			message: error.message,
		});
	}

	// Handle specific status codes with user-friendly messages
	switch (status) {
		case 400:
			// Try to extract meaningful message from response
			if (responseData?.message) {
				return `Invalid request: ${responseData.message}`;
			}
			if (responseData?.error) {
				return `Invalid request: ${responseData.error}`;
			}
			return 'The request contains invalid data. Please check your input and try again.';

		case 401:
			return 'Your session has expired. Please sign in again.';

		case 403:
			return 'You do not have permission to perform this action.';

		case 404:
			return 'The requested resource was not found.';

		case 409:
			return 'This action conflicts with existing data. Please refresh and try again.';

		case 422:
			// Validation errors
			if (responseData?.message) {
				return `Validation error: ${responseData.message}`;
			}
			if (responseData?.errors && Array.isArray(responseData.errors)) {
				return `Validation errors: ${responseData.errors.join(', ')}`;
			}
			return 'The provided data is invalid. Please check your input.';

		case 429:
			return 'Too many requests. Please wait a moment and try again.';

		case 500:
			return 'An internal server error occurred. Please try again later.';

		case 502:
			return 'Server is temporarily unavailable. Please try again in a few minutes.';

		case 503:
			return 'Service is temporarily unavailable. Please try again later.';

		case 504:
			return 'Request timed out. Please try again.';

		default:
			// For unknown status codes, use the fallback message
			// but don't expose potentially sensitive error details
			if (status >= 500) {
				return 'A server error occurred. Please try again later.';
			}
			if (status >= 400) {
				return 'There was an issue with your request. Please try again.';
			}
			return fallbackMessage;
	}
};

const handleSuccessMessage = (data: any, message: string) => {
	if (data.length === 0) {
		return 'Data not found';
	}
	return message;
};

// Helper function to determine error severity for Application Insights
const getErrorSeverity = (error: any): LogLevelType => {
	if (!error.response) {
		return LogLevel.Critical; // Network errors are critical
	}

	const status = error.response.status;
	if (status >= 500) return LogLevel.Critical; // Server errors are critical
	if (status === 429) return LogLevel.Warning; // Rate limiting is a warning
	if (status === 401 || status === 403) return LogLevel.Information; // Auth issues are informational (expected)
	if (status === 404) return LogLevel.Information; // Not found is informational (expected)
	if (status === 400 || status === 422) return LogLevel.Information; // Validation errors are informational (user input issues)
	if (status >= 400) return LogLevel.Error; // Other client errors

	return LogLevel.Error; // Default
};

// Helper function to categorize errors for better tracking
const categorizeError = (error: any): string => {
	if (!error.response) {
		if (error.code === 'NETWORK_ERROR' || error.message?.includes('Network Error')) {
			return AppInsightsErrorTypes.NETWORK_ERROR;
		}
		if (error.code === 'TIMEOUT' || error.message?.includes('timeout')) {
			return AppInsightsErrorTypes.NETWORK_ERROR;
		}
		return AppInsightsErrorTypes.NETWORK_ERROR;
	}

	const status = error.response.status;
	switch (status) {
		case 400:
		case 422:
			return AppInsightsErrorTypes.VALIDATION_ERROR;
		case 401:
			return AppInsightsErrorTypes.AUTHENTICATION_ERROR;
		case 403:
			return AppInsightsErrorTypes.AUTHORIZATION_ERROR;
		case 404:
			return AppInsightsErrorTypes.API_ERROR;
		case 409:
			return AppInsightsErrorTypes.API_ERROR;
		case 429:
			return AppInsightsErrorTypes.API_ERROR;
		case 500:
		case 502:
		case 503:
		case 504:
			return AppInsightsErrorTypes.EXTERNAL_SERVICE_ERROR;
		default:
			return AppInsightsErrorTypes.API_ERROR;
	}
};

// Helper function to determine if error should be tracked as exception or just as event
const shouldTrackErrorAsException = (error: any): boolean => {
	// Always track network errors (no response) as exceptions
	if (!error.response) {
		return true;
	}

	const status = error.response.status;

	// Use configuration to determine if this status should be tracked as exception
	return shouldTrackStatusAsException(status);
};

export const authApiV1 = axios.create({
	baseURL: window.config.HORIZON_API_URL,
	withCredentials: true, // enables sending cookies
});

export const authApiV2 = axios.create({
	baseURL: window.config.MSI_API_URL,
	withCredentials: true, // enables sending cookies
});

authApiV1.interceptors.response.use(
	(res) => res,
	async (err) => {
		const originalRequest = err.config;

		// Track auth API errors (optimized context)
		if (err.response?.status === 401) {
			trackExceptionWithContext(err, {
				component: AppInsightsComponents.AXIOS_INSTANCE,
				action: AppInsightsActions.UNAUTHORIZED_ACCESS,
				severity: LogLevel.Warning,
				additionalData: {
					api: 'authApiV1',
					statusCode: '401',
					isRetry: originalRequest._retry ? 'true' : 'false',
				},
			});
		}

		if (err.response?.status === 401 && !originalRequest._retry) {
			originalRequest._retry = true;
			try {
				const response = await axios.post(
					window.config.HORIZON_API_URL + '/token/refresh',
					{},
					{
						withCredentials: true,
					},
				);

				if (response.data === 'User must login again.') {
					redirectToSignIn();
				}
				return Promise.resolve(originalRequest);
			} catch (refreshError) {
				trackExceptionWithContext(refreshError as Error, {
					component: AppInsightsComponents.AXIOS_INSTANCE,
					action: AppInsightsActions.TOKEN_REFRESH_FAILURE,
					severity: LogLevel.Error,
					additionalData: {
						api: 'authApiV1',
						errorType: 'token_refresh_failed',
					},
				});
				redirectToSignIn();
				return Promise.reject(err);
			}
		}
		redirectToSignIn();
		return Promise.reject(err);
	},
);

// Add request interceptor to block requests when rate limited
authApiV2.interceptors.request.use(
	(config) => {
		if (isRateLimited()) {
			const remainingSeconds = getRateLimitRemainingSeconds();
			if (remainingSeconds > 0) {
				// Show rate limit overlay if not already showing
				const existingOverlay = document.getElementById('rate-limit-overlay');
				if (!existingOverlay) {
					showRateLimitOverlay();
				}
				// Reject the request
				return Promise.reject(new Error('Rate limited. Please wait before making more requests.'));
			} else {
				// Rate limit expired, clean up
				removeRateLimit();
			}
		}

		const uiPath = window.location.pathname;

		const featurePaths = ['KPIs', 'Green', 'Value', 'DCF'];

		// Search for feature in the path segments if any feature found then set the matched featurePath as feature
		const pathSegments = uiPath.split('/').filter((segment) => segment); // Remove empty segments
		let feature = 'MainPage'; // Default feature

		for (const segment of pathSegments) {
			const matchedFeaturePath = featurePaths.find((featurePath) => featurePath.toLowerCase() === segment.toLowerCase());
			if (matchedFeaturePath) {
				feature = matchedFeaturePath;
				break;
			}
		}

		config.headers['x-ui-path'] = feature;
		config.headers['X-App-Client'] = 'MSI.Seascape.App';

		return config;
	},
	(error) => Promise.reject(error),
);

authApiV2.interceptors.response.use(
	(res) => res,
	async (err) => {
		const originalRequest = err.config;

		if (err.response?.status === 429 && err.response?.data.endsAtUtc && err.response?.data.violationCountLast7Days) {
			// Track rate limit violations (optimized context)
			trackExceptionWithContext(err, {
				component: AppInsightsComponents.AXIOS_SEASCAPE_INSTANCE,
				action: AppInsightsActions.API_RESPONSE_ERROR,
				severity: LogLevel.Warning,
				additionalData: {
					api: 'authApiV2',
					statusCode: '429',
					violationCount: err.response.data.violationCountLast7Days?.toString() || 'unknown',
					errorType: 'rate_limit_violation',
				},
			});

			// if violationCountLast7Days >=3 then say that uer is locked with ModalManager.show method and redirect to sign-in on confirm
			if (err.response.data.violationCountLast7Days >= 3) {
				ModalManager.show({
					size: 'md',
					hideHeaderCloseIcon: true,
					header: 'Warning',
					actionButtonText: 'Ok',
					message: 'Your account has been locked due to too many violations.',
					showCancelButton: false,
					onConfirm: () => {
						redirectToSignIn();
					},
				});
			} else {
				// Set rate limit in localStorage
				const rateLimitUntil = new Date(err.response.data.endsAtUtc);
				setRateLimit(rateLimitUntil);

				// Show rate limit overlay with countdown
				showRateLimitOverlay();
			}

			return Promise.reject(err);
		}

		if (err.response?.status === 401 && !originalRequest._retry) {
			// Track unauthorized access for authApiV2 (optimized context)
			trackExceptionWithContext(err, {
				component: AppInsightsComponents.AXIOS_SEASCAPE_INTERCEPTOR,
				action: AppInsightsActions.UNAUTHORIZED_ACCESS,
				severity: LogLevel.Warning,
				additionalData: {
					api: 'authApiV2',
					statusCode: '401',
					isRetry: 'false',
				},
			});

			originalRequest._retry = true;
			try {
				// TODO: Commented by EKo 22/04/2025 since it should be done on popup button after error is shown in popup via catch block - discuss with team
				// const response =
				await axios.post(
					window.config.HORIZON_API_URL + '/token/refresh',
					{},
					{
						withCredentials: true,
					},
				);

				// TODO: Commented by EKo 22/04/2025 since it should be done on popup button after error is shown in popup via catch block - discuss with team
				// if (response.data === "User must login again.") {
				//     redirectToSignIn();
				// }
				return Promise.resolve(originalRequest);
			} catch (refreshError) {
				trackExceptionWithContext(refreshError as Error, {
					component: AppInsightsComponents.AXIOS_SEASCAPE_INTERCEPTOR,
					action: AppInsightsActions.TOKEN_REFRESH_FAILURE,
					severity: LogLevel.Error,
					additionalData: {
						api: 'authApiV2',
						errorType: 'token_refresh_failed',
					},
				});

				// TODO: Commented by EKo 22/04/2025 since it should be done on popup button after error is shown in popup via catch block - discuss with team
				redirectToSignIn();
				return Promise.reject(err);
			}
		}
		// TODO: Commented by EKo 22/04/2025 since it should be done on popup button after error is shown in popup via catch block
		// redirectToSignIn();

		if (err.response?.status === 500) {
			// Track server errors (optimized context)
			trackExceptionWithContext(err, {
				component: AppInsightsComponents.AXIOS_SEASCAPE_INTERCEPTOR,
				action: AppInsightsActions.API_RESPONSE_ERROR,
				severity: LogLevel.Critical,
				additionalData: {
					api: 'authApiV2',
					statusCode: '500',
					errorType: 'server_error',
					errorCode: err.response?.data?.errorCode || 'unknown',
					supportToken: err.response?.data?.supportToken || 'none',
					traceId: err.response?.data?.traceId || 'none',
				},
			});

			// Get user info from Redux store using your selector
			let userInfo: ISupportRequestUserInfo | undefined;
			try {
				const user = getUserInfoFromStore();

				if (user) {
					userInfo = {
						userId: user.userId,
						email: user.email,
						fullName: user.fullName,
						company: user.company,
					};
				}
			} catch (storeError) {
				console.warn('Failed to get user info from store:', storeError);
			}

			// Build comprehensive error message
			let emailMessage = err?.message || 'An unexpected error occurred.';
			let modalMessage = emailMessage;
			let modalMessageDetails = '';

			if (err?.response?.data?.errorCode) {
				emailMessage += `\nError Code: ${err.response.data.errorCode}`;
				modalMessageDetails += `Error Code: ${err.response.data.errorCode}`;
			}

			if (err?.response?.data?.supportToken) {
				emailMessage += `\nSupport Token: ${err.response.data.supportToken}`;
				modalMessageDetails += `\nSupport Token: ${err.response.data.supportToken}`;
			}

			if (err?.response?.data?.traceId) {
				emailMessage += `\nTrace Id: ${err.response.data.traceId}`;
				modalMessageDetails += `\nTrace Id: ${err.response.data.traceId}`;
			}

			const errorSubject = `Server Error${err?.response?.data?.errorCode ? ` - ${err?.response?.data?.requestName} Request` : ''}`;

			ModalManager.show({
				size: 'lg',
				hideHeaderCloseIcon: true,
				header: 'Unexpected Error',
				actionButtonText: 'Send to MSI',
				message: modalMessage,
				messageDetails: modalMessageDetails,
				showCancelButton: false,
				onConfirm: () => {
					emailMessage += `\nRequest Name: ${err?.response?.data?.requestName || 'unknown'}`;
					sendSupportRequestToMsi(errorSubject, emailMessage, userInfo);
				},
			});
		}
		return Promise.reject(err);
	},
);
